import { type IThemePalette } from "../types";
import { BrandColors } from "../base";

/**
 * Standard indeterminate horizontal linear progress bar tokens (e.g. ThemedProgressBar).
 */
export interface ILinearProgressTokens {
  height: number;
  trackColor: string;
  fillColor: string;
  borderRadius: number;
  zIndex: number;
  animationDuration: number;
}

/**
 * Determinate progress bar tokens for quota, upload, and capacity meters.
 */
export interface IDeterminateProgressTokens {
  height: number;
  trackColor: string;
  defaultFillColor: string;
  warningFillColor: string;
  dangerFillColor: string;
  borderRadius: number;
}

/**
 * Embedded busy progress bar for button actions.
 */
export interface IButtonProgressTokens {
  height: number;
  trackColor: string;
  fillColor: string;
}

/**
 * Audio / media player progress and buffer scrubber tokens.
 */
export interface IPlayerProgressTokens {
  height: number;
  trackColor: string;
  fillColor: string;
  bufferColor: string;
  borderRadius: number;
}

/**
 * Complete component theme definition for Progress Bars.
 */
export interface IProgressBarTheme {
  linear: ILinearProgressTokens;
  determinate: IDeterminateProgressTokens;
  button: IButtonProgressTokens;
  player: IPlayerProgressTokens;
}

/**
 * Pure factory generating the progress bar component theme based on active palette and mode.
 * Strictly adheres to the Purple Ban, WCAG contrast standards, and zero layout shift design.
 *
 * @param palette - The active semantic theme palette.
 * @param isDark - Whether dark mode is currently active.
 * @returns Fully resolved IProgressBarTheme.
 */
export function getProgressBarTheme(palette: IThemePalette, isDark: boolean): IProgressBarTheme {
  return {
    linear: {
      height: 3,
      trackColor: isDark ? "rgba(255, 255, 255, 0.12)" : palette.border,
      fillColor: palette.tint,
      borderRadius: 0,
      zIndex: 20,
      animationDuration: 1500,
    },
    determinate: {
      height: 6,
      trackColor: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(28, 35, 51, 0.1)",
      defaultFillColor: palette.tint,
      warningFillColor: BrandColors.tangerine.hex,
      dangerFillColor: BrandColors.beetRed.hex,
      borderRadius: 3,
    },
    button: {
      height: 3,
      trackColor: isDark ? "rgba(255, 255, 255, 0.18)" : "rgba(0, 0, 0, 0.12)",
      fillColor: isDark ? palette.buttonText : "#FFFFFF",
    },
    player: {
      height: 6,
      trackColor: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(28, 35, 51, 0.12)",
      fillColor: palette.tint,
      bufferColor: isDark ? "rgba(255, 255, 255, 0.25)" : "rgba(28, 35, 51, 0.2)",
      borderRadius: 3,
    },
  };
}
